import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import useInternalRedirect from 'hooks/use-internal-redirect';

export interface InternalReloadOptions {
  /** The delay in milliseconds before reloading (optional, default: 0) */
  delay?: number;
  /** Whether to show a loading spinner during the delay (optional, default: false) */
  showLoading?: boolean;
}

/**
 * Hook to reload the current page by redirecting to itself via the redirect page.
 * Preserves the full URL including search parameters.
 *
 * @example
 * const reload = useInternalReload();
 *
 * // Immediate reload
 * reload();
 *
 * // Reload after 2 seconds
 * reload({ delay: 2000 });
 *
 * // Reload after 3 seconds with loading spinner
 * reload({ delay: 3000, showLoading: true });
 */
export default function useInternalReload() {
  const location = useLocation();
  const redirect = useInternalRedirect();

  const reload = useCallback(
    (options: InternalReloadOptions = {}) => {
      const currentUrl = `${location.pathname}${location.search}`;
      redirect({
        to: currentUrl,
        delay: options.delay,
        showLoading: options.showLoading,
      });
    },
    [location.pathname, location.search, redirect]
  );

  return reload;
}
